import { useState } from 'react';
import { Badge, Button, Callout, Text, Textarea, Title } from '@tremor/react';
import { postSupportComment } from '../../api/supportTickets';
import type { SupportBoardMessage, SupportBoardTicket } from '../../api/supportTickets';
import { t } from '../../lib/i18n';
import Modal from '../../components/Modal';
import { kindColor } from './TicketBoard';

interface Props {
  ticket: SupportBoardTicket;
  /** Display name for a comment posted from this panel — the API ack only
   *  returns {id, created_at}, so the optimistic message needs the current
   *  user's name from the caller (SupportTickets already has it via /api/me). */
  authorName: string;
  onClose: () => void;
}

function stateColor(state: string): 'gray' | 'blue' | 'emerald' | 'slate' {
  switch (state) {
    case 'received':    return 'gray';
    case 'in_progress': return 'blue';
    case 'done':        return 'emerald';
    case 'closed':      return 'slate';
    default:            return 'gray';
  }
}

const fmtDateTime = new Intl.DateTimeFormat('es-MX', {
  dateStyle: 'medium', timeStyle: 'short', timeZone: 'America/Mexico_City',
});

export default function TicketDetailPanel({ ticket, authorName, onClose }: Props) {
  const [messages, setMessages] = useState<SupportBoardMessage[]>(ticket.messages);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [justSent, setJustSent] = useState(false);

  async function submit() {
    const text = body.trim();
    if (!text) return;
    setSending(true);
    setErr(null);
    try {
      const ack = await postSupportComment(ticket.identifier, text);
      setMessages((prev) => [
        ...prev,
        { id: ack.id, from: 'customer', author: authorName, body: text, created_at: ack.created_at },
      ]);
      setBody('');
      setJustSent(true);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSending(false);
    }
  }

  return (
    <Modal
      onClose={onClose}
      ariaLabel={`${t.supportTickets.detailTitle} ${ticket.identifier}`}
      variant="center"
      panelClassName="max-w-2xl p-5 space-y-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <Title><span className="font-mono">{ticket.identifier}</span> · {ticket.title}</Title>
          <div className="flex items-center gap-2 mt-1.5">
            <Badge color={stateColor(ticket.state)}>{t.supportTickets.columns[ticket.state]}</Badge>
            <Badge color={kindColor(ticket.kind)}>{t.supportTickets.kind[ticket.kind]}</Badge>
          </div>
        </div>
        <Button variant="light" onClick={onClose}>{t.common.close}</Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
        <Field label={t.supportTickets.reporter} value={ticket.reporter} />
        <Field label={t.supportTickets.created} value={fmtDateTime.format(new Date(ticket.created_at))} />
        <Field label={t.supportTickets.updated} value={fmtDateTime.format(new Date(ticket.updated_at))} />
      </div>

      <Text className="whitespace-pre-wrap">{ticket.description}</Text>

      <div>
        <Title className="text-sm">{t.supportTickets.conversation}</Title>
        <div className="space-y-2 mt-2 max-h-72 overflow-y-auto">
          {messages.length === 0 && (
            <Text className="text-tremor-content text-sm">{t.supportTickets.noMessages}</Text>
          )}
          {messages.map((m) => (
            <div
              key={m.id}
              className={
                'rounded-tremor-default p-2 text-sm ' +
                (m.from === 'team' ? 'bg-tremor-brand-faint' : 'bg-tremor-background-muted')
              }
            >
              <div className="flex justify-between text-xs text-tremor-content">
                <span className="font-medium">{m.from === 'team' ? t.supportTickets.team : m.author}</span>
                <span>{fmtDateTime.format(new Date(m.created_at))}</span>
              </div>
              <Text className="mt-0.5">{m.body}</Text>
            </div>
          ))}
        </div>
      </div>

      {err && <Callout title={t.supportTickets.commentFailed} color="rose">{err}</Callout>}

      <div className="space-y-2">
        <Textarea
          value={body}
          onChange={(e) => { setBody(e.target.value); setJustSent(false); }}
          placeholder={t.supportTickets.commentPlaceholder}
          rows={3}
          disabled={sending}
        />
        {justSent && (
          <Text className="text-emerald-600 dark:text-emerald-400 text-xs">{t.supportTickets.commentSent}</Text>
        )}
        <div className="flex justify-end">
          <Button onClick={submit} loading={sending} disabled={sending || !body.trim()}>
            {t.supportTickets.send}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-tremor-content text-xs">{label}</div>
      <div className="text-tremor-content-strong">{value}</div>
    </div>
  );
}
